import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  TimePicker,
  message
} from "antd";
import dayjs from "dayjs";
import {
  createSprNumber,
  fetchSprNumber,
  fetchSprNumberBranches,
  fetchSprNumberCategories,
  fetchSprNumberList,
  saveSprNumber
} from "./spr-number-api";
import {
  ROOT_UPPER,
  buildNumberTree,
  flattenParentOptions,
  pick,
  toNumber
} from "./spr-number-utils";

const LABEL_WIDTH = 180;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const toTime = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const timeToIso = (value) => {
  if (!value || !value.isValid()) return null;
  return value.second(0).millisecond(0).toISOString();
};

const normalizeCard = (row) => ({
  idNumber: toNumber(pick(row, "idNumber", "IdNumber")),
  idUpper: toNumber(pick(row, "idUpper", "IdUpper")) ?? ROOT_UPPER,
  name: pick(row, "name", "Name") ?? "",
  idNumberCat: toNumber(pick(row, "idNumberCat", "IdNumberCat")),
  square: toNumber(pick(row, "square", "Square")),
  timeIn: pick(row, "timeIn", "TimeIn"),
  timeOut: pick(row, "timeOut", "TimeOut"),
  idLock: toNumber(pick(row, "idLock", "IdLock")),
  idBranch: toNumber(pick(row, "idBranch", "IdBranch")),
  online: Number(pick(row, "online", "Online")) === 1,
  childCount: toNumber(pick(row, "childCount", "ChildCount")) ?? 0
});

const cardToFormValues = (card) => ({
  idNumber: card.idNumber,
  idUpper: card.idUpper == null || card.idUpper <= 0 ? ROOT_UPPER : card.idUpper,
  name: card.name,
  idNumberCat: card.idNumberCat && card.idNumberCat > 0 ? card.idNumberCat : undefined,
  square: card.square,
  timeIn: toTime(card.timeIn),
  timeOut: toTime(card.timeOut),
  idLock: card.idLock,
  idBranch: card.idBranch && card.idBranch > 0 ? card.idBranch : undefined,
  online: Boolean(card.online)
});

const emptyCard = (idUpper) => ({
  idNumber: null,
  idUpper: idUpper == null || idUpper <= 0 ? ROOT_UPPER : idUpper,
  name: "",
  idNumberCat: null,
  square: null,
  timeIn: null,
  timeOut: null,
  idLock: null,
  idBranch: null,
  online: false,
  childCount: 0
});

/**
 * Содержимое карточки номера (форма). Delphi: UESprNumber.
 */
const SprNumberCard = ({
  idNumber,
  isNew = false,
  isGroup = false,
  idUpper,
  open,
  parentOptions: parentOptionsProp,
  onSaved,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [parentOptionsLocal, setParentOptionsLocal] = useState([]);

  const parentOptions = parentOptionsProp?.length ? parentOptionsProp : parentOptionsLocal;

  const categoryOptions = useMemo(
    () =>
      (categories || [])
        .map((c) => ({
          value: toNumber(pick(c, "idNumberCat", "IdNumberCat")),
          label: pick(c, "name", "Name") ?? ""
        }))
        .filter((o) => o.value != null && o.value > 0),
    [categories]
  );

  const branchOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...(branches || [])
        .map((b) => ({
          value: toNumber(pick(b, "id", "Id")),
          label: pick(b, "name", "Name") ?? ""
        }))
        .filter((o) => o.value != null && o.value > 0)
    ],
    [branches]
  );

  const parentSelectOptions = useMemo(
    () => [
      { value: ROOT_UPPER, label: "— корень —" },
      ...(parentOptions || [])
        .filter((o) => o.value !== idNumber)
        .map((o) => ({ value: o.value, label: o.label }))
    ],
    [parentOptions, idNumber]
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.all([
      fetchSprNumberCategories(),
      fetchSprNumberBranches(),
      parentOptionsProp?.length ? Promise.resolve(null) : fetchSprNumberList()
    ])
      .then(([cats, brs, list]) => {
        if (cancelled) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setBranches(Array.isArray(brs) ? brs : []);
        if (list) setParentOptionsLocal(flattenParentOptions(buildNumberTree(list)));
      })
      .catch(() => {
        if (!cancelled) {
          setCategories([]);
          setBranches([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, parentOptionsProp]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      form.setFieldsValue(cardToFormValues(emptyCard(idUpper)));
      return () => {
        cancelled = true;
      };
    }

    if (idNumber == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprNumber(idNumber)
      .then((row) => {
        if (cancelled) return;
        form.setFieldsValue(cardToFormValues(normalizeCard(row)));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить номер");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idNumber, idUpper, form]);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = isGroup
      ? {
          idUpper: values.idUpper == null || values.idUpper <= 0 ? ROOT_UPPER : values.idUpper,
          name: (values.name || "").trim(),
          idNumberCat: 0,
          idBranch: values.idBranch && values.idBranch > 0 ? values.idBranch : 0
        }
      : {
          idUpper: values.idUpper == null || values.idUpper <= 0 ? ROOT_UPPER : values.idUpper,
          name: (values.name || "").trim(),
          idNumberCat: values.idNumberCat,
          square: values.square ?? null,
          timeIn: timeToIso(values.timeIn),
          timeOut: timeToIso(values.timeOut),
          idLock: values.idLock ?? null,
          idBranch: values.idBranch && values.idBranch > 0 ? values.idBranch : 0,
          online: values.online ? 1 : 0
        };

    try {
      if (isNew) {
        const created = await createSprNumber(body);
        message.success("Сохранено");
        onSaved?.(created);
      } else {
        const saved = await saveSprNumber({ ...body, idNumber });
        message.success("Сохранено");
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="spr-number-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  return (
    <div className="spr-number-card">
      <div className="spr-number-card__body">
        {error ? <Alert className="spr-number-card__error" type="error" showIcon message={error} /> : null}
        <Form
          form={form}
          className="spr-number-card__form"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          disabled={saving}
        >
          {!isNew ? (
            <Form.Item label="#" name="idNumber">
              <Input disabled />
            </Form.Item>
          ) : null}
          <Form.Item label="Группа" name="idUpper">
            <Select options={parentSelectOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item
            label="Наименование"
            name="name"
            rules={[{ required: true, message: "Укажите наименование" }]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item label="Филиал" name="idBranch">
            <Select allowClear options={branchOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          {isGroup ? null : (
            <>
              <Form.Item
                label="Категория"
                name="idNumberCat"
                rules={[{ required: true, message: "Укажите категорию" }]}
              >
                <Select options={categoryOptions} showSearch optionFilterProp="label" />
              </Form.Item>
              <Form.Item label="Площадь, м²" name="square">
                <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
              </Form.Item>
              <Form.Item label="Заселение" name="timeIn">
                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
              </Form.Item>
              <Form.Item label="Выселение" name="timeOut">
                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
              </Form.Item>
              <Form.Item label="Номер замка" name="idLock">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
              <Form.Item label="Продавать онлайн" name="online" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </>
          )}
        </Form>
      </div>
      <div className="spr-number-card__footer">
        <Button type="primary" loading={saving} onClick={() => form.submit()}>
          Сохранить
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprNumberCard;
